<template>
  <div>
    <BaseTableList
        :table-set-list="changeLogList"
        :display-items="logs"
        class="change-log-table">
      <div slot="message"
          slot-scope="{ row }"
          class="message-area">
        <pre class="change-log-message">
          {{ parseChangeLog(row.row) }}
        </pre>
        <div v-if="showDetailButton(row.row)"
            class="change-log-detail_button">
          <el-button size="mini"
              @click="onDetailButtonClick(row.row)">
            詳細
          </el-button>
        </div>
      </div>
    </BaseTableList>
    <SlipDialog
        :visible.sync="detailDialogVisible"
        :type="slipType"
        :detail-data="detailData"
        :detail-list-data="detailListData" />
  </div>
</template>

<style scoped>
.message-area {
  display: flex;
  justify-content: space-between;
}
.change-log-message {
  font-family: sans-serif;
  white-space: pre-line;
  margin: 0;
  width: 100%;
}
.change-log-detail_button {
  padding-left: 10px;
}
.no-data-message {
  margin: 5px 15px;
}
.dialog-contents .detail-table ::v-deep th,
.dialog-contents .detail-table ::v-deep td {
  padding: 5px 15px;
  font-size: 14px;
  text-align: left;
  vertical-align: top;
}
.dialog-contents .detail-table ::v-deep th {
  min-width: 130px;
}
.dialog-contents .detail-table-list {
  border-top: 1px solid #c7d3d9;
}
.dialog-contents .detail-table-list ::v-deep .el-table {
  box-shadow: initial;
}
</style>

<script>
import _ from 'lodash'
import moment from 'moment'
import { mapGetters } from 'vuex'

import * as define from '@/define'
import { ELEMENT_TYPE } from '@/components/molecules/BaseCell'
import BaseTableList from '@/components/molecules/BaseTableList'

import SlipDialog from '@/components/pages/order/SlipDialog'

const LOG_TYPE = {
  NOTE: {
    types: [
      201,  // メモ登録
      202   // メモ編集
    ],
    path: 'notes',
    slipType: 'note',
    showDetailList: false
  },
  EXPORT: {
    types: [
      301,  // 出荷指図登録
      305,  // 出荷指図送信
      309   // 出荷指図取消
    ],
    path: 'exports',
    slipType: 'export',
    showDetailList: true
  },
  SHIPPED: {
    types: [
      401   // 出荷実績受信
    ],
    path: 'shippeds',
    slipType: 'shipped',
    showDetailList: true
  },
  PAYMENT_SET: {
    types: [
      501,  // 支払処理
      502,  // 支払処理登録
      503,  // 支払処理更新
      509   // 支払処理取消
    ],
    path: 'payments.paymentSets',
    slipType: 'paymentSet',
    showDetailList: false
  }
}

export default {
  name: 'OrderDetailLogs',
  components: {
    BaseTableList,
    SlipDialog
  },
  props: {
    latestOrder: {
      type: Object,
      default: () => ({})
    }
  },
  data () {
    return {
      detailDialogVisible: false,
      detailData: {},
      detailDataType: null
    }
  },
  computed: {
    define: () => define,
    ...mapGetters({
      getServiceSiteConfig: 'getServiceSiteConfig'
    }),
    ...mapGetters('OrderDetail', {
      siteCode: 'siteCode'
    }),
    logs () {
      return _.chain(this.latestOrder.logs)
        .sortBy([val => moment(val.createdAt)])
        .reverse()
        .value()
    },
    changeLogList () {
      return [
        { label: '登録日時', path: 'createdAt', type: 'Date', width: 200,
          funcId: define.PermissionKeys.Order.Detail.Log.CREATED_AT },
        { label: '履歴種別', path: 'logType', codeName: 'j_order_log.log_type', width: 200,
          funcId: define.PermissionKeys.Order.Detail.Log.TYPE },
        { label: '変更内容', type: 'Slot', path: 'message', class: 'change-log' },
        {
          label: '更新者', type: 'String', elementType: ELEMENT_TYPE.FUNCTION, width: 250,
          path: (row) => {
            return row.account == null || row.account === ''
              ? 'システム' : row.account
          },
          funcId: define.PermissionKeys.Order.Detail.Log.CREATED_BY
        }
      ]
    },
    logType () {
      return _.find(LOG_TYPE, val => _.includes(val.types, this.detailDataType))
    },
    slipType () {
      return _.get(this.logType, 'slipType')
    },
    detailListData () {
      return _.chain(this.logType)
        .get('showDetailList')
        .thru(val => {
          return val ? _.get(this.detailData, 'items') : null
        })
        .value()
    }
  },
  watch: {
    detailDialogVisible (val) {
      if (!val) {
        this.detailData = {}
        this.detailDataType = null
      }
    }
  },
  methods: {
    parseChangeLog (row) {
      try {
        const log = JSON.parse(row.changeLog.replace(/(\t|\r\n|\r|\n)+/g, ' '))
        const message = _.chain([])
          .concat(log)
          .map(val => {
            if (!_.has(val, 'tbl') || !_.has(val, 'fld')) {
              // TODO 内容が不正だった場合、暫定で変更内容非表示
              // return JSON.stringify(val)
              return null
            }

            // 項目名取得
            const fieldName = this.getChangeLogFieldName(val.tbl, val.fld)
            if (fieldName == null || fieldName === '') {
              return null
            }

            let str = ''
            if (_.has(val, 'frm') && _.has(val, 'to')) {
              // 更新の場合
              str = `${fieldName} を ${val.frm} から ${val.to} に編集`
            } else if (!_.has(val, 'frm') && _.has(val, 'to')) {
              // 登録の場合
              str = `${fieldName} ${val.to} を登録`
            } else if (_.has(val, 'frm') && !_.has(val, 'to')) {
              // 削除の場合
              str = `${fieldName} ${val.frm} を削除`
            }
            if (_.has(val, 'opt')) {
              // オプション表示ありの場合
              str += (this.getChangeLogOption(val.tbl, `${val.fld}_opt`, val.opt) || '')
            }
            if (str !== '') {
              return str
            }
            // TODO 内容が不正だった場合、暫定で変更内容非表示
            // return JSON.stringify(val)
            return null
          })
          .filter(val => val != null)
          .join('\n')
          .value()
        return message
      } catch (error) {
        // TODO parseできなかった場合、暫定で変更内容非表示
        // return row.changeLog
        return ''
      }
    },
    getChangeLogFieldName (table, field) {
      return _.get(
        this.getServiceSiteConfig(null, 'j_order_log', table, field),
        'label'
      )
    },
    getChangeLogOption (table, optKey, opt) {
      const str = (_.get(
        this.getServiceSiteConfig(null, 'j_order_log', table, optKey),
        'label'
      ) || '')
      if (str !== '') {
        const split = str.split('"')
        return split.map((v) => {
          if (v === '*') {
            return `${opt}`
          }
          return v
        }).join('')
      }
      return `${opt}`
    },
    showDetailButton (row) {
      return _.find(LOG_TYPE, val => _.includes(val.types, row.logType)) != null && this.$isFunctionShown(define.PermissionKeys.Order.Detail.Log.DETAIL)
    },
    onDetailButtonClick (row) {
      const path = _.chain(LOG_TYPE)
        .find(val => _.includes(val.types, row.logType))
        .get('path')
        .value()
      if (path) {
        this.detailData = this.getDetailData(path, row.refid)
      }
      this.detailDataType = row.logType
      this.detailDialogVisible = true
    },
    getDetailData (path, refid) {
      return _.chain(this.latestOrder)
        .thru(val => {
          const paths = _.split(path, '.')
          let data = val
          for (let i = 0; i < paths.length; i++) {
            if (Array.isArray(data)) {
              data = _.flatMap(data, val => _.get(val, paths[i]))
            } else {
              data = _.get(val, paths[i])
            }
          }
          return data
        })
        .find(val => val.id === refid)
        .value()
    }
  }
}
</script>
